<template>
  <div class="panel-body">
    <HeaderGeneral :vista="vista" :ruta="proveniente" :carpeta="carpeta" />

    <!-- Filtros -->
    <b-card class="mb-3 filtros-card">
      <b-row>
        <b-col md="2">
          <b-form-group label="ID auditoría">
            <b-form-input
              type="number"
              v-model="filtros.id"
              placeholder="Ej: 1024"
              @keyup.enter="buscar"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="ID usuario">
            <b-form-input
              type="number"
              v-model="filtros.id_usuario"
              placeholder="Ej: 15"
              @keyup.enter="buscar"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Usuario (correo)">
            <b-form-input
              v-model="filtros.nombre_usuario"
              placeholder="Ej: jefeliderzipa@aygserviciosdesalud.com"
              @keyup.enter="buscar"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="URL / Endpoint">
            <b-form-input
              v-model="filtros.url"
              placeholder="Buscar por URL..."
              @keyup.enter="buscar"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Método HTTP">
            <b-form-select v-model="filtros.metodo" :options="metodosOptions" />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Código HTTP">
            <b-form-select v-model="filtros.statusCode" :options="statusOptions" />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Fecha desde">
            <b-form-input type="date" v-model="filtros.fecha_desde" />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Fecha hasta">
            <b-form-input type="date" v-model="filtros.fecha_hasta" />
          </b-form-group>
        </b-col>
        <b-col md="1" class="d-flex align-items-end pb-3">
          <b-button variant="primary" @click="buscar" class="mr-1">
            <b-icon icon="search" />
          </b-button>
          <b-button variant="outline-secondary" @click="limpiarFiltros">
            <b-icon icon="x" />
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Tabla -->
    <b-table
      responsive
      id="tabla-auditoria"
      head-variant="light"
      striped
      :items="registros"
      :fields="fields"
      :busy="cargando"
      show-empty
      empty-text="No se encontraron registros"
    >
      <template #table-busy>
        <div class="text-center my-3">
          <b-spinner variant="primary" />
        </div>
      </template>

      <template #cell(metodo)="row">
        <span :class="['badge-metodo', 'badge-metodo--' + (row.item.metodo || 'unknown').toLowerCase()]">
          {{ row.item.metodo || '-' }}
        </span>
      </template>

      <template #cell(status_code)="row">
        <span :class="['badge-status', classeStatus(row.item.status_code)]">
          {{ row.item.status_code || '-' }}
        </span>
      </template>

      <template #cell(nombre_usuario)="row">
        <span v-if="row.item.nombre_usuario">
          <b-icon icon="person-fill" class="mr-1" />
          {{ row.item.nombre_usuario }}
          <small class="text-muted d-block">{{ row.item.email_usuario }}</small>
        </span>
        <span v-else class="text-muted">Anónimo</span>
      </template>

      <template #cell(fecha)="row">
        {{ formatFecha(row.item.fecha) }}
      </template>

      <template #cell(url)="row">
        <span class="text-truncate-url" :title="row.item.url">{{ row.item.url }}</span>
      </template>

      <template #cell(acciones)="row">
        <b-button
          size="sm"
          variant="info"
          @click="verDetalle(row.item)"
          v-b-tooltip.hover
          title="Ver detalle completo"
        >
          <b-icon icon="eye-fill" />
        </b-button>
      </template>
    </b-table>

    <div class="divnodata" v-if="registros.length === 0 && !cargando">
      <b-img class="bannernodata" src="/img/banner.jpg" />
      <p>No se encontraron registros de auditoría</p>
    </div>

    <!-- Paginación -->
    <b-row class="mt-2">
      <b-col md="2" offset-md="8" class="text-right">
        <label>Registros por página:</label>
      </b-col>
      <b-col md="2">
        <b-form-select
          v-model="filtros.size"
          :options="[10, 20, 50, 100]"
          @change="buscar"
        />
      </b-col>
    </b-row>
    <b-pagination
      class="justify-content-center mt-2"
      v-model="filtros.page"
      :total-rows="total"
      :per-page="filtros.size"
      @change="onPageChange"
    />

    <!-- Modal detalle -->
    <b-modal
      id="modal-detalle-auditoria"
      v-model="showModal"
      title="Detalle de la petición"
      size="xl"
      scrollable
      ok-only
      ok-title="Cerrar"
    >
      <div v-if="cargandoDetalle" class="text-center py-4">
        <b-spinner variant="primary" />
      </div>

      <div v-else-if="detalle">
        <!-- Resumen -->
        <b-row class="mb-3">
          <b-col md="6">
            <b-list-group>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                <strong>Método</strong>
                <span :class="['badge-metodo', 'badge-metodo--' + ((detalle.data && detalle.data.metodo) || 'unknown').toLowerCase()]">
                  {{ (detalle.data && detalle.data.metodo) || '-' }}
                </span>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between align-items-center">
                <strong>Código HTTP</strong>
                <span :class="['badge-status', classeStatus(detalle.data && detalle.data.statusCode)]">
                  {{ (detalle.data && detalle.data.statusCode) || '-' }}
                </span>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between">
                <strong>URL</strong>
                <span class="text-break ml-2">{{ detalle.data && detalle.data.url }}</span>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between">
                <strong>IP</strong>
                <span>{{ detalle.data && detalle.data.ip }}</span>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between">
                <strong>Fecha</strong>
                <span>{{ formatFecha(detalle.data && detalle.data.fecha) }}</span>
              </b-list-group-item>
            </b-list-group>
          </b-col>
          <b-col md="6">
            <b-list-group>
              <b-list-group-item class="d-flex justify-content-between" v-if="detalle.nombre_usuario">
                <strong>Usuario</strong>
                <span>{{ detalle.nombre_usuario }}</span>
              </b-list-group-item>
              <b-list-group-item class="d-flex justify-content-between" v-if="detalle.email_usuario">
                <strong>Email</strong>
                <span>{{ detalle.email_usuario }}</span>
              </b-list-group-item>
              <b-list-group-item
                class="d-flex justify-content-between"
                v-if="detalle.data && detalle.data.usuario"
              >
                <strong>ID Usuario</strong>
                <span>{{ detalle.data.usuario.id_usuario }}</span>
              </b-list-group-item>
              <b-list-group-item
                class="d-flex justify-content-between"
                v-if="detalle.data && detalle.data.usuario"
              >
                <strong>Tenant</strong>
                <span>{{ detalle.data.usuario.nombre_tenant }}</span>
              </b-list-group-item>
              <b-list-group-item
                class="d-flex justify-content-between"
                v-if="detalle.data && detalle.data.usuario"
              >
                <strong>Módulo</strong>
                <span>{{ detalle.data.usuario.nombre_modulo }}</span>
              </b-list-group-item>
            </b-list-group>
          </b-col>
        </b-row>

        <!-- Tabs para body, query, response -->
        <b-tabs content-class="mt-3">
          <b-tab title="Body (Request)">
            <div class="json-tree-container">
              <json-tree v-if="detalle.data && detalle.data.body != null" :data="detalle.data.body" />
              <span v-else class="jt-null">-</span>
            </div>
          </b-tab>
          <b-tab title="Query Params">
            <div class="json-tree-container">
              <json-tree v-if="detalle.data && detalle.data.query != null" :data="detalle.data.query" />
              <span v-else class="jt-null">-</span>
            </div>
          </b-tab>
          <b-tab title="Response Body">
            <div class="json-tree-container">
              <json-tree v-if="detalle.data && detalle.data.responseBody != null" :data="detalle.data.responseBody" />
              <span v-else class="jt-null">-</span>
            </div>
          </b-tab>
          <b-tab title="Headers">
            <div class="json-tree-container">
              <json-tree v-if="detalle.data && detalle.data.headers != null" :data="detalle.data.headers" />
              <span v-else class="jt-null">-</span>
            </div>
          </b-tab>
          <b-tab title="JSON Completo">
            <div class="json-tree-container">
              <json-tree v-if="detalle.data != null" :data="detalle.data" />
              <span v-else class="jt-null">-</span>
            </div>
          </b-tab>
        </b-tabs>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { RepositoryFactory } from "../../repositories/RepositoryFactory";
import HeaderGeneral from "../../components/HeaderGeneral.vue";

const auditoriaRepo = RepositoryFactory.get("auditoria");

const JsonTree = {
  name: "JsonTree",
  props: {
    data: {},
    depth: { type: Number, default: 0 },
  },
  data() {
    return { open: this.depth < 2 };
  },
  methods: {
    toggle() { this.open = !this.open; },
    getType(val) {
      if (val === null) return "null";
      if (Array.isArray(val)) return "array";
      return typeof val;
    },
    getEntries(val, type) {
      if (type === "array") return val.map((v, i) => ({ key: i, val: v }));
      if (type === "object") return Object.entries(val).map(([k, v]) => ({ key: k, val: v }));
      return [];
    },
  },
  render(h) {
    const val = this.data;
    const type = this.getType(val);
    const isComplex = type === "object" || type === "array";

    if (!isComplex) {
      let cls = "jt-null", text = "null";
      if (type === "string")  { cls = "jt-string";  text = `"${val}"`; }
      else if (type === "number")  { cls = "jt-number";  text = String(val); }
      else if (type === "boolean") { cls = "jt-bool";    text = String(val); }
      return h("span", { class: "jt-node" }, [h("span", { class: cls }, text)]);
    }

    const entries = this.getEntries(val, type);
    const openBracket  = type === "array" ? "[" : "{";
    const closeBracket = type === "array" ? "]" : "}";

    const children = [];

    // Toggle button
    children.push(h("button", {
      class: "jt-toggle",
      attrs: { title: this.open ? "Colapsar" : "Expandir" },
      on: { click: this.toggle },
    }, [this.open ? "▼" : "▶"]));

    // Opening bracket
    children.push(h("span", { class: "jt-bracket" }, openBracket));

    if (!this.open) {
      // Collapsed summary
      children.push(h("span", {
        class: "jt-summary",
        on: { click: this.toggle },
      }, [
        h("span", { class: "jt-count" }, `${entries.length} ${type === "array" ? "items" : "props"}`),
      ]));
      children.push(h("span", { class: "jt-bracket" }, closeBracket));
    } else {
      // Expanded children
      const rows = entries.map((entry, i) => {
        const keyLabel = type === "array" ? String(entry.key) : `"${entry.key}"`;
        return h("div", { key: entry.key, class: "jt-entry" }, [
          h("span", { class: "jt-key" }, keyLabel),
          h("span", { class: "jt-colon" }, ": "),
          h(JsonTree, { props: { data: entry.val, depth: this.depth + 1 } }),
          i < entries.length - 1 ? h("span", { class: "jt-comma" }, ",") : null,
        ]);
      });
      children.push(h("div", { class: "jt-children" }, rows));
      children.push(h("div", { class: "jt-bracket" }, closeBracket));
    }

    return h("span", { class: "jt-node" }, children);
  },
};

export default {
  components: { HeaderGeneral, JsonTree },

  data() {
    return {
      vista: "Bitácora de Auditoría",
      proveniente: " - Configuración",
      carpeta: "Configuración",

      registros: [],
      total: 0,
      cargando: false,

      filtros: {
        page: 1,
        size: 20,
        id: "",
        id_usuario: "",
        nombre_usuario: "",
        url: "",
        metodo: null,
        statusCode: null,
        fecha_desde: "",
        fecha_hasta: "",
      },

      metodosOptions: [
        { value: null, text: "Todos los métodos" },
        { value: "GET", text: "GET" },
        { value: "POST", text: "POST" },
        { value: "PUT", text: "PUT" },
        { value: "DELETE", text: "DELETE" },
        { value: "PATCH", text: "PATCH" },
        { value: "EMAIL", text: "EMAIL (correos automáticos)" },
      ],

      statusOptions: [
        { value: null, text: "Todos los códigos" },
        {
          label: "── Agrupados ──",
          options: [
            { value: "RANGE_4xx",        text: "Todos los 4xx  (errores de cliente)" },
            { value: "RANGE_4xx_sin_404", text: "4xx sin 404  (errores sin Not Found)" },
            { value: "RANGE_5xx",        text: "Todos los 5xx  (errores de servidor)" },
          ],
        },
        {
          label: "2xx – Éxito",
          options: [
            { value: "200", text: "200 - OK" },
            { value: "201", text: "201 - Created" },
            { value: "202", text: "202 - Accepted" },
            { value: "204", text: "204 - No Content" },
            { value: "206", text: "206 - Partial Content" },
          ],
        },
        {
          label: "3xx – Redirección",
          options: [
            { value: "301", text: "301 - Moved Permanently" },
            { value: "302", text: "302 - Found" },
            { value: "304", text: "304 - Not Modified" },
            { value: "307", text: "307 - Temporary Redirect" },
            { value: "308", text: "308 - Permanent Redirect" },
          ],
        },
        {
          label: "4xx – Error del cliente",
          options: [
            { value: "400", text: "400 - Bad Request" },
            { value: "401", text: "401 - Unauthorized" },
            { value: "403", text: "403 - Forbidden" },
            { value: "404", text: "404 - Not Found" },
            { value: "405", text: "405 - Method Not Allowed" },
            { value: "408", text: "408 - Request Timeout" },
            { value: "409", text: "409 - Conflict" },
            { value: "410", text: "410 - Gone" },
            { value: "413", text: "413 - Payload Too Large" },
            { value: "415", text: "415 - Unsupported Media Type" },
            { value: "422", text: "422 - Unprocessable Entity" },
            { value: "429", text: "429 - Too Many Requests" },
          ],
        },
        {
          label: "5xx – Error del servidor",
          options: [
            { value: "500", text: "500 - Internal Server Error" },
            { value: "501", text: "501 - Not Implemented" },
            { value: "502", text: "502 - Bad Gateway" },
            { value: "503", text: "503 - Service Unavailable" },
            { value: "504", text: "504 - Gateway Timeout" },
            { value: "507", text: "507 - Insufficient Storage" },
          ],
        },
      ],

      fields: [
        { key: "id", label: "ID", sortable: false, thStyle: { width: "60px" } },
        { key: "fecha", label: "Fecha", sortable: false },
        { key: "metodo", label: "Método", sortable: false, thStyle: { width: "90px" } },
        { key: "status_code", label: "HTTP", sortable: false, thStyle: { width: "80px" } },
        { key: "url", label: "URL / Endpoint", sortable: false },
        { key: "nombre_usuario", label: "Usuario", sortable: false },
        { key: "acciones", label: "Acciones", sortable: false, thStyle: { width: "80px" } },
      ],

      showModal: false,
      detalle: null,
      cargandoDetalle: false,
    };
  },

  mounted() {
    this.buscar();
  },

  methods: {
    async buscar() {
      this.cargando = true;
      this.filtros.page = 1;
      try {
        const res = await auditoriaRepo.getAuditorias(this.buildParams());
        if (res && res.payload) {
          this.registros = res.payload.records || [];
          this.total = res.payload.total || 0;
        }
      } catch (err) {
        console.error(err);
        this.$bvToast.toast("Error al cargar la bitácora", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.cargando = false;
      }
    },

    async onPageChange(page) {
      this.filtros.page = page;
      this.cargando = true;
      try {
        const res = await auditoriaRepo.getAuditorias(this.buildParams());
        if (res && res.payload) {
          this.registros = res.payload.records || [];
          this.total = res.payload.total || 0;
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.cargando = false;
      }
    },

    buildParams() {
      const p = { ...this.filtros };
      if (!p.id) delete p.id;
      if (!p.id_usuario) delete p.id_usuario;
      if (!p.nombre_usuario) delete p.nombre_usuario;
      if (!p.url) delete p.url;
      if (!p.metodo) delete p.metodo;
      if (!p.fecha_desde) delete p.fecha_desde;
      if (!p.fecha_hasta) delete p.fecha_hasta;

      // Manejar códigos agrupados vs código exacto
      if (!p.statusCode) {
        delete p.statusCode;
      } else if (String(p.statusCode).startsWith('RANGE_')) {
        p.statusCodeRange = p.statusCode.replace('RANGE_', '').toLowerCase();
        delete p.statusCode;
      }
      return p;
    },

    limpiarFiltros() {
      this.filtros = {
        page: 1,
        size: 20,
        id: "",
        id_usuario: "",
        nombre_usuario: "",
        url: "",
        metodo: null,
        statusCode: null,
        fecha_desde: "",
        fecha_hasta: "",
      };
      this.buscar();
    },

    async verDetalle(item) {
      this.detalle = null;
      this.showModal = true;
      this.cargandoDetalle = true;
      try {
        const res = await auditoriaRepo.getAuditoriaById(item.id);
        if (res && res.payload) {
          this.detalle = res.payload;
        }
      } catch (err) {
        console.error(err);
        this.$bvToast.toast("Error al cargar el detalle", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.cargandoDetalle = false;
      }
    },

    formatFecha(fecha) {
      if (!fecha) return "-";
      const d = new Date(fecha);
      if (isNaN(d)) return fecha;
      return d.toLocaleString("es-CO", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },

    formatJson(val) {
      if (!val) return "-";
      if (typeof val === "string") {
        try {
          return JSON.stringify(JSON.parse(val), null, 2);
        } catch {
          return val;
        }
      }
      return JSON.stringify(val, null, 2);
    },

    classeStatus(code) {
      const n = parseInt(code);
      if (n >= 200 && n < 300) return "badge-status--2xx";
      if (n >= 300 && n < 400) return "badge-status--3xx";
      if (n >= 400 && n < 500) return "badge-status--4xx";
      if (n >= 500) return "badge-status--5xx";
      return "badge-status--unknown";
    },
  },
};
</script>

<style scoped>
.filtros-card {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
}

.json-pre {
  background: #1e1e1e;
  color: #d4d4d4;
  border-radius: 6px;
  padding: 16px;
  font-size: 12px;
  max-height: 350px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

.text-truncate-url {
  display: inline-block;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.text-break {
  word-break: break-all;
}

/* ── Badges de método HTTP ── */
.badge-metodo {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #fff;
  text-transform: uppercase;
}
.badge-metodo--get    { background-color: #2ecc71; }
.badge-metodo--post   { background-color: #3498db; }
.badge-metodo--put    { background-color: #e67e22; }
.badge-metodo--delete { background-color: #e74c3c; }
.badge-metodo--patch  { background-color: #9b59b6; }
.badge-metodo--unknown { background-color: #95a5a6; }

/* ── Badges de código HTTP ── */
.badge-status {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
}
.badge-status--2xx     { background-color: #2ecc71; }
.badge-status--3xx     { background-color: #3498db; }
.badge-status--4xx     { background-color: #e67e22; }
.badge-status--5xx     { background-color: #e74c3c; }
.badge-status--unknown { background-color: #95a5a6; }

/* ── JSON Tree collapsible ── */
.json-tree-container {
  background: #1e1e1e;
  border-radius: 6px;
  padding: 14px 16px;
  font-size: 12px;
  max-height: 420px;
  overflow-y: auto;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  line-height: 1.65;
}
.jt-node { display: inline; color: #d4d4d4; }
.jt-toggle {
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  font-size: 9px;
  padding: 0 4px 0 0;
  line-height: 1;
  vertical-align: middle;
  outline: none;
}
.jt-toggle:hover { color: #ddd; }
.jt-bracket { color: #d4d4d4; }
.jt-summary { cursor: pointer; }
.jt-count {
  color: #6a9955;
  font-style: italic;
  padding: 0 6px;
  font-size: 11px;
}
.jt-summary:hover .jt-count { color: #9cdcfe; }
.jt-children { margin-left: 20px; border-left: 1px solid #333; padding-left: 8px; }
.jt-entry { display: block; }
.jt-key   { color: #9cdcfe; }
.jt-colon { color: #d4d4d4; }
.jt-comma { color: #d4d4d4; }
.jt-string { color: #ce9178; }
.jt-number { color: #b5cea8; }
.jt-bool   { color: #569cd6; }
.jt-null   { color: #808080; font-style: italic; }
</style>
